import { TextField, InputAdornment, IconButton, Stack, Tooltip } from '@mui/material';
import Search from '@mui/icons-material/Search';
import Clear from '@mui/icons-material/Clear';
import { useFlags } from 'launchdarkly-react-client-sdk';
import Button from 'domains/core/components/Button';

export type Props = {
    placeholder?: string;
    tooltipText?: string;
    value: string;
    onChange: (value: string) => void;
    onSearch: (value: string) => void;
    dataTestId?: string;
    variant?: 'standard' | 'outlined' | 'filled';
    minWidth?: number;
    disabled?: boolean;
};

/**
 * ExactSearchField - A search field that triggers search on button click or Enter key press.
 * Unlike SearchField which searches on every keystroke, this component waits for explicit user action.
 * Features:
 * - Search icon at the start
 * - Clear (X) button when there's text
 * - Search button to trigger search
 * - Enter key support
 * - Auto-clears search results when input is cleared
 */
const ExactSearchField = ({
    placeholder = 'Search...',
    tooltipText,
    value,
    onChange,
    onSearch,
    dataTestId,
    variant = 'standard',
    minWidth = 250,
    disabled,
}: Props) => {
    const { exactSearch: hasExactSearchFlag } = useFlags();

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            onSearch(value);
        }
    };

    const handleClear = () => {
        onChange('');
        onSearch('');
    };

    if (!hasExactSearchFlag) {
        return null;
    }

    const searchField = (
        <Stack direction="row" alignItems="center" spacing={1}>
            <TextField
                variant={variant}
                placeholder={placeholder}
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search color={disabled ? 'disabled' : 'inherit'} />
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <InputAdornment position="end" sx={{ width: 28, justifyContent: 'center' }}>
                            {value && (
                                <IconButton
                                    size="small"
                                    onClick={handleClear}
                                    disabled={disabled}
                                    data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
                                    aria-label="clear search"
                                >
                                    <Clear fontSize="small" />
                                </IconButton>
                            )}
                        </InputAdornment>
                    ),
                }}
                sx={{ minWidth }}
                data-testid={dataTestId}
            />
            <Button
                onClick={() => onSearch(value)}
                data-testid={dataTestId ? `${dataTestId}-button` : undefined}
                variant="outlined"
                size="small"
                disabled={disabled}
            >
                Search
            </Button>
        </Stack>
    );

    if (tooltipText) {
        return (
            <Tooltip title={tooltipText} placement="bottom" arrow>
                {searchField}
            </Tooltip>
        );
    }

    return searchField;
};

export default ExactSearchField;
